import {
  getBrowseItem,
  getGreetingItem,
  getSubscriptionFeedbackItem,
  getSubscriptionRenewalItem,
  getFeatureIntroductionItem,
  getFeatureAwarenessItem,
  getSubscriptionFeedbackItemV2,
  getFreeTrialItem,
  getPaidTrialItem
} from '@cont/PlannerV2/planner-utils/data-processor/items-generator';
import styled from '@emotion/styled';
import { getIsFinalSetupCompleteV2 } from '@stores/selectors/planner';
import { Spacings, mediaQuery } from '@styles/index';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { useHelperFlagsStoreV2 } from '@stores/planner/helperFlagsStore';
import Row from './Row';
import { plannerItemTypes } from './planner-constants';

// Explaination for below computation of width:
// 350px - RightWrapper width
// ((100vw - ${Spacings.APP_CONTAINER_WIDTH}) / 2) - Additional width planner
// will be occupying to compensate for the ful page scrolling effect.
const Wrapper = styled.div`
  max-width: calc(
    100% - 350px - ((100vw - ${Spacings.APP_CONTAINER_WIDTH}) / 2)
  );
  max-height: ${({ $height }) => $height};
  overflow-y: scroll;
  padding-right: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_4B};

  ${mediaQuery.uptoMobile} {
    max-width: 100%;
  }
`;

const MemoizedDay = memo(
  ({
    dayInfo,
    eventsCount,
    showBrowse = false,
    todayKey,
    featureCards = []
  }) => {
    const isFinalSetupComplete = useHelperFlagsStoreV2(
      getIsFinalSetupCompleteV2()
    );

    const showHeaderAndFooter = dayInfo?.dateKey === todayKey;

    let dayInfoItems = dayInfo?.metaItems;
    if (!dayInfoItems || dayInfoItems?.length <= 0) {
      return null;
    }
    let headerMetaItem = null;
    const featureCardItems = [];
    if (showHeaderAndFooter) {
      headerMetaItem = showBrowse ? getBrowseItem() : getGreetingItem();
      featureCards?.forEach((card) => {
        if (card.card_type === plannerItemTypes.SUBSCRIPTION_RENEWAL) {
          featureCardItems.push(getSubscriptionRenewalItem(card.item_info));
        } else if (
          card.card_type === plannerItemTypes.FEATURE_CARD &&
          !!card.header_text
        ) {
          featureCardItems.push(getFeatureIntroductionItem(card));
        } else if (card.card_type === plannerItemTypes.FEATURE_CARD) {
          featureCardItems.push(getFeatureAwarenessItem(card));
        } else if (card.card_type === plannerItemTypes.SUBSCRIPTION_FEEDBACK) {
          featureCardItems.push(getSubscriptionFeedbackItem());
        } else if (
          card.card_type === plannerItemTypes.SUBSCRIPTION_FEEDBACK_V2
        ) {
          featureCardItems.push(getSubscriptionFeedbackItemV2(card.item_info));
        } else if (card.card_type === plannerItemTypes.FREE_TRIAL_CARD) {
          featureCardItems.push(getFreeTrialItem(card));
        } else if (
          card.card_type === plannerItemTypes.PAID_TRIAL_SUBSCRIPTION
        ) {
          featureCardItems.push(getPaidTrialItem(card));
        }
      });
      const [dayTitleItem, ...allOtherItems] = dayInfoItems;
      dayInfoItems = [dayTitleItem, headerMetaItem, ...allOtherItems];
      if (featureCardItems?.length > 0) {
        featureCardItems.forEach((featureCard) =>
          dayInfoItems.push(featureCard)
        );
      }
    }

    const loaderItems = [];
    if (!isFinalSetupComplete) {
      loaderItems.push(dayInfoItems[0]);
      loaderItems.push({
        plannerItemType: plannerItemTypes.SKELETON_LOADER
      });
    }

    if (dayInfoItems.length < 2 && isFinalSetupComplete) {
      dayInfoItems[1] = {
        plannerItemType: plannerItemTypes.NO_SESSION_EVENTS
      };
    }

    const renderRowItems = (rowItems) => {
      return rowItems.map((metaItem) => (
        <Row
          key={metaItem.uid || metaItem.title}
          metaItem={metaItem}
          eventsCount={eventsCount}
        />
      ));
    };

    dayInfoItems.sort((a, b) => (a.activityAt || 0) - (b.activityAt || 0));
    const filteredData =
      dayInfoItems[1]?.plannerItemType === 'browse'
        ? dayInfoItems.filter(
            (item) => item.plannerItemType !== 'subscription_feedback_v2'
          )
        : dayInfoItems;
    return (
      <>
        {isFinalSetupComplete
          ? renderRowItems(filteredData)
          : renderRowItems(loaderItems)}
      </>
    );
  }
);

MemoizedDay.displayName = 'MemoizedDay';
MemoizedDay.propTypes = {
  showBrowse: PropTypes.bool.isRequired,
  featureCards: PropTypes.arrayOf(PropTypes.shape({})),
  todayKey: PropTypes.string.isRequired,
  eventsCount: PropTypes.number.isRequired,
  dayInfo: PropTypes.shape({}).isRequired
};

MemoizedDay.defaultProps = {
  featureCards: []
};

const getPlannerBodyHeight = () => {
  const layoutHeaderHeight = document.querySelector('header').offsetHeight;
  return `calc(100vh - ${layoutHeaderHeight}px)`;
};

const Day = ({ selectedDateKey, style, data }) => {
  const { plannerItemsByDaysV2, showBrowse, todayKey, featureCards } = data;

  const eventsCount = plannerItemsByDaysV2?.filter(
    (metaItem) => !!metaItem.eventType
  ).length;

  return (
    <Wrapper
      style={style}
      key={selectedDateKey}
      $height={getPlannerBodyHeight()}
    >
      <MemoizedDay
        eventsCount={eventsCount}
        dayInfo={{
          dateKey: selectedDateKey,
          metaItems: plannerItemsByDaysV2
        }}
        showBrowse={showBrowse}
        todayKey={todayKey}
        featureCards={featureCards}
      />
    </Wrapper>
  );
};

export default Day;
